import React from 'react';
import { FormRenderProps } from '@progress/kendo-react-form';
import {
  buildPerFileFormValues,
  buildPerFileFormValuesWithClearedDependents,
  clearPerFileDependentMetadata,
  getDependentFields,
  getFileItemKey,
  isValueEmpty,
  scopedFieldKey
} from './FieldInteractionsHelper';
import { toFormFieldValue } from '../fields/formFieldValueHelpers';
import type {
  FileWithPerFileMetadata,
  UsePerFileFieldInteractionsProps,
  UsePerFileFieldInteractionsReturn
} from './fieldInteractionsTypes';
import { useFieldState } from './useFieldState';
import { useCurrentUserContext, useLoadFieldValues } from '@cs-cms/state-management';
import { ContentTypeFieldResponse, CpsDocumentMetadata, CpsDocumentMetadataValue, LoadFieldValuesRequest } from '@cs-cms/types';

export const usePerFileFieldInteractions = ({
  ctFields,
  perFileFields,
  selectedCtName
}: UsePerFileFieldInteractionsProps): UsePerFileFieldInteractionsReturn => {
  const fieldState = useFieldState();
  const { selectedIndex } = useCurrentUserContext();
  const { mutateAsync: loadFieldValues } = useLoadFieldValues();
  const syncedFileKeysRef = React.useRef<Set<string>>(new Set());
  const inFlightKeysRef = React.useRef<Set<string>>(new Set());

  const ctFieldsRef = React.useRef(ctFields);
  const perFileFieldsRef = React.useRef(perFileFields);
  const selectedCtNameRef = React.useRef(selectedCtName);
  const selectedIndexIdRef = React.useRef(selectedIndex?.id);

  ctFieldsRef.current = ctFields;
  perFileFieldsRef.current = perFileFields;
  selectedCtNameRef.current = selectedCtName;
  selectedIndexIdRef.current = selectedIndex?.id;

  const callLoadFieldValuesApi = React.useCallback(
    async (fieldName: string, currentFormValues: CpsDocumentMetadata) => {
      const currentPerFileFields = perFileFieldsRef.current;
      const currentCtFields = ctFieldsRef.current;
      const field =
        currentPerFileFields.find(f => f.name === fieldName) ?? currentCtFields.find(f => f.name === fieldName);
      const contentTypeName = selectedCtNameRef.current;
      const indexId = selectedIndexIdRef.current;

      if (!field || !contentTypeName || indexId == null) {
        return null;
      }

      const request: LoadFieldValuesRequest = {
        contentTypeName,
        metadata: currentFormValues
      };

      const fieldId = field.id ?? -1;
      return await loadFieldValues({ fieldId, request });
    },
    [loadFieldValues]
  );

  const loadDependentChoiceOptionsForFile = React.useCallback(
    async (
      fileKey: string,
      fileIndex: number,
      dependentFieldName: string,
      currentFormValues: CpsDocumentMetadata,
      files: FileWithPerFileMetadata[],
      formRenderProps: FormRenderProps
    ): Promise<FileWithPerFileMetadata[]> => {
      const currentPerFileFields = perFileFieldsRef.current;
      const dependentField = currentPerFileFields.find(f => f.name === dependentFieldName);
      if (!dependentField?.usesExtDictionary) {
        return files;
      }

      const fieldScopeKey = scopedFieldKey(fileKey, dependentFieldName);

      if (inFlightKeysRef.current.has(fieldScopeKey)) {
        return files;
      }

      inFlightKeysRef.current.add(fieldScopeKey);
      fieldState.addLoadingField(fieldScopeKey);

      let updatedFiles = files;

      try {
        const response = await callLoadFieldValuesApi(dependentFieldName, currentFormValues);

        if (response?.values && response.values.length > 0) {
          fieldState.setFieldChoices(fieldScopeKey, response.values);
          fieldState.addEnabledField(fieldScopeKey);
          syncedFileKeysRef.current.add(fieldScopeKey);

          if (response.values.length === 1) {
            const autoFilledValue = response.values[0];
            const formValue: CpsDocumentMetadataValue = toFormFieldValue(dependentField, autoFilledValue);
            const perFileMetadata: CpsDocumentMetadata = {
              ...(updatedFiles[fileIndex].perFileMetadata ?? {})
            };
            perFileMetadata[dependentFieldName] = formValue;
            updatedFiles = [...files];
            updatedFiles[fileIndex] = {
              ...updatedFiles[fileIndex],
              perFileMetadata
            };

            const dependentFields = getDependentFields(dependentFieldName, currentPerFileFields);
            if (dependentFields.length > 0) {
              const clearedMetadata = clearPerFileDependentMetadata(
                updatedFiles[fileIndex].perFileMetadata,
                dependentFields
              );
              const nextFormValues = buildPerFileFormValuesWithClearedDependents(
                formRenderProps,
                ctFieldsRef.current,
                clearedMetadata,
                dependentFieldName,
                autoFilledValue,
                dependentFields
              );

              for (const nestedDependentField of dependentFields) {
                updatedFiles = await loadDependentChoiceOptionsForFile(
                  fileKey,
                  fileIndex,
                  nestedDependentField,
                  nextFormValues,
                  updatedFiles,
                  formRenderProps
                );
              }
            }
          }
        } else {
          fieldState.clearFieldChoices(fieldScopeKey);
          fieldState.removeEnabledField(fieldScopeKey);
          syncedFileKeysRef.current.delete(fieldScopeKey);
        }
      } finally {
        inFlightKeysRef.current.delete(fieldScopeKey);
        fieldState.removeLoadingField(fieldScopeKey);
      }

      return updatedFiles;
    },
    [callLoadFieldValuesApi, fieldState]
  );

  const handleDependenciesForFile = React.useCallback(
    async (
      fileKey: string,
      fileIndex: number,
      fieldName: string,
      value: CpsDocumentMetadata[string],
      files: FileWithPerFileMetadata[],
      formRenderProps: FormRenderProps
    ): Promise<FileWithPerFileMetadata[]> => {
      const currentPerFileFields = perFileFieldsRef.current;
      const dependentFields = getDependentFields(fieldName, currentPerFileFields);
      if (dependentFields.length === 0) {
        return files;
      }

      const fileItem = files[fileIndex];
      const clearedMetadata = clearPerFileDependentMetadata(fileItem.perFileMetadata, dependentFields);

      let updatedFiles = [...files];
      updatedFiles[fileIndex] = {
        ...updatedFiles[fileIndex],
        perFileMetadata: {
          ...clearedMetadata,
          [fieldName]: value
        }
      };

      dependentFields.forEach(dependentField => {
        const fieldScopeKey = scopedFieldKey(fileKey, dependentField);
        fieldState.clearFieldChoices(fieldScopeKey);
        fieldState.removeEnabledField(fieldScopeKey);
        syncedFileKeysRef.current.delete(fieldScopeKey);
        inFlightKeysRef.current.delete(fieldScopeKey);
      });

      const currentFormValues = buildPerFileFormValuesWithClearedDependents(
        formRenderProps,
        ctFieldsRef.current,
        clearedMetadata,
        fieldName,
        value,
        dependentFields
      );

      for (const dependentField of dependentFields) {
        updatedFiles = await loadDependentChoiceOptionsForFile(
          fileKey,
          fileIndex,
          dependentField,
          currentFormValues,
          updatedFiles,
          formRenderProps
        );
      }

      return updatedFiles;
    },
    [fieldState, loadDependentChoiceOptionsForFile]
  );

  const handlePerFileFieldChange = React.useCallback(
    async (
      fileIndex: number,
      fieldName: string,
      value: CpsDocumentMetadata[string],
      files: FileWithPerFileMetadata[],
      formRenderProps: FormRenderProps
    ): Promise<FileWithPerFileMetadata[]> => {
      const fileItem = files[fileIndex];
      if (!fileItem) {
        return files;
      }

      const field = perFileFieldsRef.current.find(f => f.name === fieldName);
      if (!field) {
        return files;
      }

      const fileKey = getFileItemKey(fileItem);
      const updatedFiles = [...files];
      updatedFiles[fileIndex] = {
        ...updatedFiles[fileIndex],
        perFileMetadata: {
          ...updatedFiles[fileIndex].perFileMetadata,
          [fieldName]: value
        }
      };

      return await handleDependenciesForFile(fileKey, fileIndex, fieldName, value, updatedFiles, formRenderProps);
    },
    [handleDependenciesForFile]
  );

  const areParentFieldsSet = (field: ContentTypeFieldResponse, perFileMetadata: CpsDocumentMetadata): boolean => {
    if (!field.dependsOnFields?.length) {
      return true;
    }

    return field.dependsOnFields.every(parentFieldName => !isValueEmpty(perFileMetadata[parentFieldName]));
  };

  const syncPerFileFieldInteractions = React.useCallback(
    async (files: FileWithPerFileMetadata[], formRenderProps: FormRenderProps): Promise<void> => {
      const currentPerFileFields = perFileFieldsRef.current;

      for (let fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
        const fileItem = files[fileIndex];
        const fileKey = getFileItemKey(fileItem);
        const perFileMetadata = fileItem.perFileMetadata || {};

        for (const field of currentPerFileFields) {
          if (!field.dependsOnFields?.length || !field.usesExtDictionary) {
            continue;
          }

          if (!areParentFieldsSet(field, perFileMetadata)) {
            continue;
          }

          const fieldScopeKey = scopedFieldKey(fileKey, field.name);
          if (syncedFileKeysRef.current.has(fieldScopeKey) || inFlightKeysRef.current.has(fieldScopeKey)) {
            continue;
          }

          const currentFormValues = buildPerFileFormValues(formRenderProps, ctFieldsRef.current, perFileMetadata);
          await loadDependentChoiceOptionsForFile(
            fileKey,
            fileIndex,
            field.name,
            currentFormValues,
            files,
            formRenderProps
          );
        }
      }
    },
    [loadDependentChoiceOptionsForFile]
  );

  const removePerFileFieldState = React.useCallback(
    (fileKey: string): void => {
      perFileFieldsRef.current.forEach(field => {
        const fieldScopeKey = scopedFieldKey(fileKey, field.name);
        syncedFileKeysRef.current.delete(fieldScopeKey);
        inFlightKeysRef.current.delete(fieldScopeKey);
        fieldState.clearFieldChoices(fieldScopeKey);
        fieldState.removeEnabledField(fieldScopeKey);
        fieldState.removeLoadingField(fieldScopeKey);
      });
    },
    [fieldState]
  );

  const getPerFileFieldChoices = (fileKey: string, field: ContentTypeFieldResponse): string[] => {
    const fieldScopeKey = scopedFieldKey(fileKey, field.name);
    if (field.usesExtDictionary && fieldState.dynamicChoices[fieldScopeKey]) {
      return fieldState.dynamicChoices[fieldScopeKey];
    }
    return field.choices || [];
  };

  const isPerFileFieldEnabled = (fileKey: string, field: ContentTypeFieldResponse): boolean => {
    if (!field.dependsOnFields?.length) {
      return true;
    }

    return fieldState.enabledFields.has(scopedFieldKey(fileKey, field.name));
  };

  const isPerFileFieldLoading = (fileKey: string, field: ContentTypeFieldResponse): boolean => {
    return fieldState.loadingFields.has(scopedFieldKey(fileKey, field.name));
  };

  return {
    getPerFileFieldChoices,
    isPerFileFieldEnabled,
    isPerFileFieldLoading,
    handlePerFileFieldChange,
    syncPerFileFieldInteractions,
    removePerFileFieldState
  };
};
